/**
 * component-announcement.js — Announcement Bar Rotation Web Component
 *
 * Rotates between multiple announcement messages with fade animation.
 * Pauses on hover and when document is hidden (tab not in focus).
 * Only loaded when section.blocks.size > 1 (from announcement-bar.liquid).
 *
 * Animation: sequential fade — current fades fully out, THEN next fades in.
 * (A crossfade overlaps two different-length centred messages and reads as
 * garbled double-text.) Driven by a single recursive timeout so the cadence
 * can never drift or race the fade.
 */

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this._currentIndex = 0;
    this._running = false;
    this._timer = null;
    this._transitionTimeout = null;
    this._boundPause = this._pause.bind(this);
    this._boundResume = this._resume.bind(this);
    this._boundVisibility = this._handleVisibility.bind(this);
  }

  connectedCallback() {
    this._messages = Array.from(this.querySelectorAll('[data-announcement-message]'));

    // No rotation needed for 0 or 1 messages
    if (this._messages.length <= 1) return;

    // Read speed from data attribute (set by Liquid: section.settings.speed * 1000)
    this._speed = parseInt(this.dataset.speed || '5000', 10);

    // Fade duration, read from the same CSS token the transition uses (--duration-fast)
    // so JS and CSS stay in lockstep. Small buffer so the swap lands after the fade.
    const dur = getComputedStyle(document.documentElement)
      .getPropertyValue('--duration-fast').trim();
    const ms = dur.endsWith('ms') ? parseFloat(dur) : parseFloat(dur) * 1000;
    this._fadeMs = (ms || 150) + 30;

    // Ensure first message is active
    this._messages.forEach((msg, i) => {
      msg.classList.toggle('announcement--active', i === 0);
    });
    this._currentIndex = 0;

    // Start rotation
    this._startRotation();

    // Pause on hover
    this.addEventListener('mouseenter', this._boundPause);
    this.addEventListener('mouseleave', this._boundResume);

    // Pause when tab is not in focus
    document.addEventListener('visibilitychange', this._boundVisibility);

    // Theme editor section reload compatibility
    this.addEventListener('section:load', () => {
      this.disconnectedCallback();
      this.connectedCallback();
    });
  }

  disconnectedCallback() {
    this._stopRotation();
    this.removeEventListener('mouseenter', this._boundPause);
    this.removeEventListener('mouseleave', this._boundResume);
    document.removeEventListener('visibilitychange', this._boundVisibility);
  }

  _startRotation() {
    this._running = true;
    this._scheduleNext();
  }

  _stopRotation() {
    this._running = false;
    // Cancel the pending tick. Any in-flight fade is left to finish on its own —
    // it checks _running before re-arming, so it won't keep rotating, and the
    // message is never left half-faded (blank).
    clearTimeout(this._timer);
    this._timer = null;
  }

  _scheduleNext() {
    if (!this._running) return;
    clearTimeout(this._timer); // single timer at all times — no stacked ticks
    this._timer = setTimeout(() => this._next(), this._speed);
  }

  _pause() {
    this._stopRotation();
  }

  _resume() {
    if (!document.hidden) {
      this._startRotation();
    }
  }

  _handleVisibility() {
    if (document.hidden) {
      this._stopRotation();
    } else if (!this.matches(':hover')) {
      this._startRotation();
    }
  }

  _next() {
    const currentMsg = this._messages[this._currentIndex];
    const nextIndex = (this._currentIndex + 1) % this._messages.length;
    const nextMsg = this._messages[nextIndex];

    // Step 1: fade the current message fully out.
    currentMsg.classList.remove('announcement--active');
    currentMsg.classList.add('announcement--exiting');

    // Step 2: only once it's gone, bring the next one in — no overlap.
    this._transitionTimeout = setTimeout(() => {
      currentMsg.classList.remove('announcement--exiting');
      nextMsg.classList.add('announcement--active');
      this._currentIndex = nextIndex;
      this._transitionTimeout = null;
      // Re-arm from here, so the full display interval is measured from the
      // moment the new message appears — uniform cadence, immune to drift.
      this._scheduleNext();
    }, this._fadeMs);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
